/**
 * Validates if a redirect URL is safe to use
 * Prevents malicious redirects to external sites or auth loops
 */
export const isValidRedirect = (url: string | null): boolean => {
  if (!url || typeof url !== "string") return false;

  // Must start with /
  if (!url.startsWith("/")) return false;

  // Must not be an external URL disguised as internal (//evil.com)
  if (url.startsWith("//")) return false;

  // Must not be an auth route (avoid redirect loops)
  if (url.includes("/auth/")) return false;

  // Must not be the onboarding route (avoid redirect loops)
  if (url.includes("/onboarding")) return false;

  return true;
};

/**
 * Checks if a URL should have a redirect parameter added
 * Returns false for:
 * - Home page (/)
 * - Auth pages (/auth/*)
 */
export const shouldAddRedirect = (url: string): boolean => {
  // Don't add redirect for home page
  if (url === "/" || url === "") {
    return false;
  }

  // Don't add redirect if already on an auth page (including /auth/login)
  if (url.includes("/auth/")) {
    return false;
  }

  return true;
};

/**
 * Builds the login URL with optional redirect parameter
 * Centralized logic for constructing login redirects
 *
 * @param locale - User's locale (e.g., "fr", "en")
 * @param currentPath - Current path WITHOUT locale (e.g., "/store", "/auth/login")
 * @returns Login URL with or without redirect parameter
 */
export const buildLoginUrlWithRedirect = (locale: string, currentPath: string): string => {
  const baseUrl = `/${locale}/auth/login`;

  // Don't add redirect parameter if:
  // 1. Current path is home page
  // 2. Current path is already an auth page (avoid loops)
  if (!shouldAddRedirect(currentPath)) {
    return baseUrl;
  }

  // Add redirect parameter for protected pages
  const redirectParam = encodeURIComponent(currentPath);
  return `${baseUrl}?redirect=${redirectParam}`;
};
